<script setup>
import {ref, getCurrentInstance, computed} from 'vue'
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import ProgressBar from 'primevue/progressbar';
import InputText from 'primevue/inputtext';
import InputSwitch from 'primevue/inputswitch';
import axios from 'axios';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    importFailures: Object,
    importErrors: Object,
    importAdded: Array,
    importUpdated: Array,
    newSellersCreated: Array,
    availableCurrencies: {
        type: Array,
        required: true
    },
    exchangeData: {
        type: Object,
        required: true
    }
});

const { showPageLoading, hidePageLoading } = useEnhancedLoading('admin-sites-import')

/**
 * Calculate suggested price based on cost and currency using dynamic exchange data
 * This replaces the hardcoded logic with values from the database
 *
 * Two formulas are supported:
 * 1. BRL (apply_percentage_to_total = false): cost + (cost × percentage) + fixed
 * 2. EUR/USD/GBP (apply_percentage_to_total = true): (cost + fixed) × (1 + percentage)
 *
 * @param {number} cost - Cost value (in cents or formatted)
 * @param {string} currency - Currency code (BRL, USD, EUR, GBP, etc.)
 * @returns {number} Suggested price
 */
const computeSuggestedPrice = (cost, currency) => {
    // Convert cost to number if it's a string (handle both formats: "200,00" and "200.00")
    let costValue = cost
    if (typeof cost === 'string') {
        // Remove currency symbols and spaces
        let cleaned = cost.replace(/^(R\$|USD|\$|EUR|€|BRL)\s*/gi, '').replace(/\s*(R\$|USD|\$|EUR|€|BRL)$/gi, '').trim()
        // Remove thousands separators and convert comma to dot
        cleaned = cleaned.replace(/\./g, '').replace(',', '.')
        costValue = parseFloat(cleaned)
    }

    if (isNaN(costValue) || costValue <= 0) {
        return 0
    }

    // Get exchange data for this currency
    const exchange = props.exchangeData[currency.toUpperCase()]

    if (!exchange) {
        // If no exchange data, return cost as-is
        return costValue
    }

    // Get fixed addition, percentage addition, and formula type from exchange data
    const fixedAddition = exchange.fixed_addition || 0
    const percentageAddition = exchange.percentage_addition || 0
    const applyPercentageToTotal = exchange.apply_percentage_to_total || false

    let suggested

    if (applyPercentageToTotal) {
        // Formula for EUR/USD/GBP
        suggested = (costValue + (fixedAddition / 100)) * (1 + (percentageAddition / 10000))
    } else {
        // Formula for BRL
        suggested = costValue + (costValue * (percentageAddition / 10000)) + (fixedAddition / 100)
    }

    return suggested
}

/**
 * Convert value to base currency (USD) for fair comparison between different currencies
 * Uses exchange rates from backend database (exchange table)
 *
 * @param {number|string} value - Value in original currency (formatted like "200,00")
 * @param {string} fromCurrency - Source currency code (BRL, EUR, USD, GBP, etc.)
 * @returns {number} Value converted to USD for comparison
 */
const convertToUSD = (value, fromCurrency) => {
    if (!value || isValueEmpty(value)) return 0

    // Parse value if it's a string (handles format like "200,00")
    let numValue = value
    if (typeof value === 'string') {
        const cleaned = value.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
        numValue = parseFloat(cleaned)
    }

    if (isNaN(numValue) || numValue <= 0) return 0

    // If already USD, return as-is
    const currencyUpper = fromCurrency.toUpperCase()
    if (currencyUpper === 'USD') return numValue

    // To convert from another currency to USD: value / (exchange.value / 100)
    const fromExchange = props.exchangeData[currencyUpper]
    const usdExchange = props.exchangeData['USD']

    if (!fromExchange || !usdExchange) {
        console.warn(`Exchange rate not found for ${currencyUpper}, using 1:1`)
        return numValue
    }

    const convertedValue = numValue * (usdExchange.value / fromExchange.value)

    return convertedValue
}

/**
 * Calculate the highest cost and sale from all type fields
 *
 * This function:
 * 1. Iterates through all type fields (c_geral, c_cassino, c_bets, etc.)
 * 2. Converts each value to USD for fair cross-currency comparison
 * 3. Returns the highest cost and highest sale in their original currencies
 *
 * @param {object} data - Row data containing all type fields
 * @returns {object} { highestCost, highestCostCoin, highestSale, highestSaleCoin }
 */
const calculateHighestDisplayValues = (data) => {
    let highestCostUSD = 0
    let highestCost = ''
    let highestCostCoin = data.moeda || 'BRL'

    let highestSaleUSD = 0
    let highestSale = ''
    let highestSaleCoin = data.moeda || 'BRL'

    // Check all type fields (c_geral, c_cassino, c_bets, c_cbd, c_crip, c_dating, c_erotic)
    Object.values(typeFieldMap).forEach(typeFields => {
        const costValue = data[typeFields.cost]
        const saleValue = data[typeFields.sale]
        const costCoin = data[typeFields.costCoin] || data.moeda || 'BRL'
        const saleCoin = data[typeFields.saleCoin] || data.moeda || 'BRL'

        // Compare costs - convert to USD for fair comparison between different currencies
        if (!isValueEmpty(costValue)) {
            const costInUSD = convertToUSD(costValue, costCoin)

            if (costInUSD > highestCostUSD) {
                highestCostUSD = costInUSD
                highestCost = costValue
                highestCostCoin = costCoin
            }
        }

        // Compare sales - convert to USD for fair comparison between different currencies
        if (!isValueEmpty(saleValue)) {
            const saleInUSD = convertToUSD(saleValue, saleCoin)

            if (saleInUSD > highestSaleUSD) {
                highestSaleUSD = saleInUSD
                highestSale = saleValue
                highestSaleCoin = saleCoin
            }
        }
    })

    return {
        highestCost: highestCost || '',
        highestCostCoin,
        highestSale: highestSale || '',
        highestSaleCoin
    }
}

// ============================================================================
// MODAL CONFIRMATION FOR DISPLAY VALUE UPDATES
// ============================================================================
// When a user manually edits the display sale value, and then changes a type.sale
// that results in a higher value, we show a modal asking if they want to:
// 1. Keep their manually edited display value
// 2. Update to the new higher type.sale value
// ============================================================================

const showDisplayValueModal = ref(false)
const displayValueModalData = ref(null)

/**
 * User chose to keep their current manually-edited display value
 */
const keepCurrentDisplayValue = () => {
    showDisplayValueModal.value = false
    displayValueModalData.value = null
}

/**
 * User chose to update display value to the new higher type.sale value
 * This also resets the manual edit flag so future changes auto-update
 */
const updateToNewDisplayValue = () => {
    if (displayValueModalData.value) {
        const { data, newSaleValue, newSaleCoin } = displayValueModalData.value
        data['venda_de_exibicao'] = newSaleValue
        data['venda_de_exibicao_coin'] = newSaleCoin
        data._displaySaleManuallyEdited = false // Reset manual edit flag
    }
    showDisplayValueModal.value = false
    displayValueModalData.value = null
}


const showDisplayCostModal = ref(false)
const displayCostModalData = ref(null)

const keepCurrentDisplayCost = () => {
    showDisplayCostModal.value = false
    displayCostModalData.value = null
}

const updateToNewDisplayCost = () => {
    if (displayCostModalData.value) {
        const { data, newCostValue, newCostCoin } = displayCostModalData.value
        data['custo_de_exibicao'] = newCostValue
        data['custo_de_exibicao_coin'] = newCostCoin
        data._displayCostManuallyEdited = false // Reset manual edit flag
    }
    showDisplayCostModal.value = false
    displayCostModalData.value = null
}

// ============================================================================
// IMPORT PROGRESS MODAL
// ============================================================================

const showImportProgressModal = ref(false)
const importProgressCurrent = ref(0)
const importProgressTotal = ref(0)
const importProgressMessage = ref('')

// Modal para sellers novos
const showNewSellersModal = ref(false)
const newSellers = ref([])
const existingSellers = ref([])
const newSellersEmails = ref({})

// Dados detalhados do resumo final
const importSummary = ref(null)

// ============================================================================
// IMPORT STATE & CONFIGURATION
// ============================================================================

const importFilepond = ref(null)
const importFinished = ref(false)
const notifyAdded = ref(false)
const notifyUpdated = ref(false)
const ignoreEmptySiteFields = ref(false)
const importPricingOnly = ref(false) // Toggle: true = Import Pricing Only, false = Import Site Data
const uploadedFile = ref(null)
const previewData = ref(null)
const isLoadingPreview = ref(false)
const filePath = ref(null)
const previewColumns = ref([])
const activeStatusFilter = ref('ALL') // ALL, NEW, UPDATE, FAILED
const sellers = ref([])

// ============================================================================
// PRICING-ONLY MODE CONFIGURATION
// ============================================================================
// Fields that are shown when importing pricing only (essential + pricing fields)
const pricingOnlyFields = [
    'url', 'sites', 'seller', 'responsavel', 'moeda',
    'c_bets', 'v_bets', 'c_cassino', 'v_cassino', 'c_geral', 'v_geral',
    'c_cbd', 'v_cbd', 'c_crip', 'v_crip', 'c_dating', 'v_dating',
    'c_erotic', 'v_erotic', 'c_loan', 'v_loan', 'c_forex', 'v_forex',
    'custo_de_exibicao', 'venda_de_exibicao'
]

// Computed property to filter columns based on import mode
const visibleColumns = computed(() => {
    if (!previewColumns.value) return []

    if (importPricingOnly.value) {
        // When importing pricing only, show only pricing-related columns
        return previewColumns.value.filter(col => pricingOnlyFields.includes(col.toLowerCase()))
    }

    // When importing site data, show all columns
    return previewColumns.value
})

// ============================================================================
// TYPE FIELDS CONFIGURATION
// ============================================================================
// Type price fields that should be formatted without currency symbol

const typeFields = ['c_bets', 'v_bets', 'c_cassino', 'v_cassino', 'c_geral', 'v_geral', 'c_cbd', 'v_cbd', 'c_crip', 'v_crip', 'c_dating', 'v_dating', 'c_erotic', 'v_erotic', 'c_loan', 'v_loan', 'c_forex', 'v_forex']

const anchorFields = ['accepted_anchors']

// Cost fields (when edited, should trigger sale auto-calculation)
const costFields = ['c_bets', 'c_cassino', 'c_geral', 'c_cbd', 'c_crip', 'c_dating', 'c_erotic', 'c_loan', 'c_forex']

// Sale fields (calculated automatically from cost)
const saleFields = ['v_bets', 'v_cassino', 'v_geral', 'v_cbd', 'v_crip', 'v_dating', 'v_erotic', 'v_loan', 'v_forex']

// Map of type fields to their indices for easier lookup
const typeFieldMap = {
    'c_geral': { cost: 'c_geral', sale: 'v_geral', costCoin: 'c_geral_coin', saleCoin: 'v_geral_coin' },
    'c_cassino': { cost: 'c_cassino', sale: 'v_cassino', costCoin: 'c_cassino_coin', saleCoin: 'v_cassino_coin' },
    'c_bets': { cost: 'c_bets', sale: 'v_bets', costCoin: 'c_bets_coin', saleCoin: 'v_bets_coin' },
    'c_cbd': { cost: 'c_cbd', sale: 'v_cbd', costCoin: 'c_cbd_coin', saleCoin: 'v_cbd_coin' },
    'c_crip': { cost: 'c_crip', sale: 'v_crip', costCoin: 'c_crip_coin', saleCoin: 'v_crip_coin' },
    'c_dating': { cost: 'c_dating', sale: 'v_dating', costCoin: 'c_dating_coin', saleCoin: 'v_dating_coin' },
    'c_erotic': { cost: 'c_erotic', sale: 'v_erotic', costCoin: 'c_erotic_coin', saleCoin: 'v_erotic_coin' },
    'c_loan': { cost: 'c_loan', sale: 'v_loan', costCoin: 'c_loan_coin', saleCoin: 'v_loan_coin' },
    'c_forex': { cost: 'c_forex', sale: 'v_forex', costCoin: 'c_forex_coin', saleCoin: 'v_forex_coin' }
}

// Type currency fields (companion fields to type prices)
const typeCurrencyFields = ['c_bets_coin', 'v_bets_coin', 'c_cassino_coin', 'v_cassino_coin', 'c_geral_coin', 'v_geral_coin', 'c_cbd_coin', 'v_cbd_coin', 'c_crip_coin', 'v_crip_coin', 'c_dating_coin', 'v_dating_coin', 'c_erotic_coin', 'v_erotic_coin', 'c_loan_coin', 'v_loan_coin', 'c_forex_coin', 'v_forex_coin']

// Helper to check if a field is a type price field
const isTypeField = (field) => typeFields.includes(field)

// Helper to check if a field is a cost field
const isCostField = (field) => costFields.includes(field)

// Helper to check if a field is a sale field
const isSaleField = (field) => saleFields.includes(field)

const isSellerLinkarme = (sellerName) => {
    if (!sellerName || !sellers.value) return false

    const seller = sellers.value.find(s =>
        s.name.toLowerCase().includes(sellerName.toLowerCase())
    )

    return seller ? seller.linkarme_seller : false
}

const isSaleFieldReadOnly = (data, field) => {
    if (!isSaleField(field)) return false

    const sellerName = data.seller || data.responsavel
    if (!sellerName) return true

    return !isSellerLinkarme(sellerName)
}

const isAnchorField = (field) => anchorFields.includes(field)

const validateAnchorValue = (value) => {
    const numValue = parseInt(value, 10)

    if (isNaN(numValue) || numValue < 1 || numValue > 3) {
        return 1
    }

    return numValue
}

// Helper to check if a field is a currency field
const isCurrencyField = (field) => typeCurrencyFields.includes(field)

const shouldHideColumn = (columnName) => {
    if (!columnName || typeof columnName !== 'string') return false

    const lowerName = columnName.toLowerCase()
    return lowerName.includes('sugestao') ||
           lowerName.includes('sugestão') ||
           lowerName.includes('responsavel') ||
           lowerName.includes('responsável') ||
           lowerName === 'sugestao_de_valor' ||
           lowerName === 'sugestao de valor' ||
           lowerName === 'sugestão de valor' ||
           lowerName === 'sugestão_de_valor' ||
           lowerName === 'responsavel' ||
           lowerName === 'responsável'
}

const hasRealChanges = (data) => {
    if (!data) return false

    if (data.status !== 'UPDATE' || !data._existing) {
        return data.status === 'NEW'
    }

    if (!previewColumns.value || previewColumns.value.length === 0) {
        return false
    }

    return visibleColumns.value.some(col => {
        if (shouldHideColumn(col)) return false
        try {
            const comparison = getComparisonValue(data, col)
            return comparison.hasChange
        } catch (error) {
            console.warn('Error checking comparison for column:', col, error)
            return false
        }
    })
}

const shouldShowStatus = (data) => {
    try {
        return hasRealChanges(data)
    } catch (error) {
        return data.status === 'NEW' || data.status === 'UPDATE'
    }
}

// ============================================================================
// FIELD NAME HELPERS
// ============================================================================

// Helper to get the corresponding sale field for a cost field
const getSaleFieldName = (costField) => {
    return costField.replace('c_', 'v_')
}

// Helper to get the corresponding cost field for a sale field
const getCostFieldName = (saleField) => {
    return saleField.replace('v_', 'c_')
}

// Helper to get the currency field name for a type field
const getCurrencyFieldName = (typeField) => {
    return typeField + '_coin'
}

// ============================================================================
// VALUE FORMATTING HELPERS
// ============================================================================

/**
 * Format value with currency for display only (not for editing)
 * Example: "200,00 USD"
 */
const formatValueWithCurrency = (value, currency) => {
    if (!value || value === '') return ''
    const formattedValue = formatNumericValue(value)
    return currency ? `${formattedValue} ${currency}` : formattedValue
}

/**
 * Format numeric values to Brazilian format (0,00) WITHOUT currency symbols
 *
 * This function:
 * 1. Removes all currency symbols (R$, $, €, USD, EUR, BRL, etc.)
 * 2. Handles multiple input formats (200.00, 200,00, 1.200,00, 1,200.00)
 * 3. Always outputs in Brazilian format: 200,00
 *
 * Examples:
 * - "R$ 200.00" -> "200,00"
 * - "$200.00" -> "200,00"
 * - "200 USD" -> "200,00"
 * - "1,200.50" -> "1.200,50"
 */
const formatNumericValue = (value) => {
    if (!value || value === '' || value === null || value === undefined) {
        return ''
    }

    // Convert to string
    let strValue = String(value).trim()

    // If empty after trim, return empty
    if (strValue === '') return ''

    // Remove currency symbols (R$, $, €, USD, EUR, BRL, GBP, etc.)
    strValue = strValue.replace(/^(R\$|USD|\$|EUR|€|BRL|GBP|£)\s*/gi, '')
    strValue = strValue.replace(/\s*(R\$|USD|\$|EUR|€|BRL|GBP|£)$/gi, '')

    // Remove any non-numeric characters except comma and dot
    strValue = strValue.replace(/[^\d,.-]/g, '')

    // If no numeric value left, return empty
    if (!strValue || strValue === '') return ''

    // Parse the number - handle both dot and comma as decimal separators
    let numValue

    // Check if it's already in correct format (e.g., "200,00")
    if (strValue.includes(',') && !strValue.includes('.')) {
        // Format: 200,00 or 1.200,00
        strValue = strValue.replace(/\./g, '') // Remove thousands separator
        numValue = parseFloat(strValue.replace(',', '.'))
    } else if (strValue.includes('.') && !strValue.includes(',')) {
        // Format: 200.00 or 1,200.00
        strValue = strValue.replace(/,/g, '') // Remove thousands separator
        numValue = parseFloat(strValue)
    } else if (strValue.includes('.') && strValue.includes(',')) {
        // Format could be 1.200,00 or 1,200.00
        const dotPos = strValue.lastIndexOf('.')
        const commaPos = strValue.lastIndexOf(',')

        if (commaPos > dotPos) {
            // Format: 1.200,00 (European)
            strValue = strValue.replace(/\./g, '').replace(',', '.')
        } else {
            // Format: 1,200.00 (US)
            strValue = strValue.replace(/,/g, '')
        }
        numValue = parseFloat(strValue)
    } else {
        // No decimal separator, treat as integer
        numValue = parseFloat(strValue)
    }

    // If not a valid number, return empty
    if (isNaN(numValue)) return ''

    // Format to Brazilian format: 0,00
    return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })
}

// Helper function to normalize type values (removes currency symbols and formats to 0,00)
const normalizeTypeValue = (value, field) => {
    if (!typeFields.includes(field)) {
        return value
    }

    return formatNumericValue(value)
}

// Computed property to filter preview data based on active filter
const filteredPreviewData = computed(() => {
    if (!previewData.value) return []

    if (activeStatusFilter.value === 'ALL') {
        return previewData.value
    } else if (activeStatusFilter.value === 'FAILED') {
        return previewData.value.filter(d => d._failed)
    } else if (activeStatusFilter.value === 'NEW') {
        return previewData.value.filter(d => {
            try {
                return hasRealChanges(d) && d.status === 'NEW' && !d._failed && !d._success
            } catch (error) {
                return d.status === 'NEW' && !d._failed && !d._success
            }
        })
    } else if (activeStatusFilter.value === 'UPDATE') {
        return previewData.value.filter(d => {
            try {
                return hasRealChanges(d) && d.status === 'UPDATE' && !d._failed && !d._success
            } catch (error) {
                return d.status === 'UPDATE' && !d._failed && !d._success
            }
        })
    }
    return previewData.value
})

// Computed counts for selected items
const selectedCounts = computed(() => {
    if (!previewData.value) return { total: 0, new: 0, update: 0, failed: 0 }

    return {
        total: previewData.value.filter(d => d._selected).length,
        new: previewData.value.filter(d => {
            try {
                return d._selected && hasRealChanges(d) && d.status === 'NEW' && !d._failed && !d._success
            } catch (error) {
                return d._selected && d.status === 'NEW' && !d._failed && !d._success
            }
        }).length,
        update: previewData.value.filter(d => {
            try {
                return d._selected && hasRealChanges(d) && d.status === 'UPDATE' && !d._failed && !d._success
            } catch (error) {
                return d._selected && d.status === 'UPDATE' && !d._failed && !d._success
            }
        }).length,
        failed: previewData.value.filter(d => d._selected && d._failed).length,
    }
})

const totalCounts = computed(() => {
    if (!previewData.value) return { total: 0, new: 0, update: 0, failed: 0 }

    return {
        total: previewData.value.length,
        new: previewData.value.filter(d => {
            try {
                return hasRealChanges(d) && d.status === 'NEW' && !d._failed && !d._success
            } catch (error) {
                return d.status === 'NEW' && !d._failed && !d._success
            }
        }).length,
        update: previewData.value.filter(d => {
            try {
                return hasRealChanges(d) && d.status === 'UPDATE' && !d._failed && !d._success
            } catch (error) {
                return d.status === 'UPDATE' && !d._failed && !d._success
            }
        }).length,
        failed: previewData.value.filter(d => d._failed).length,
    }
})

const uploadSites = async (event) => {
    const file = event.files[0]
    if (!file) return

    isLoadingPreview.value = true
    showPageLoading($t('Processing file...'))

    try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await axios.post(proxy.route('sites.import.preview'), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })

        if (response.data.success) {
            // Add _selected property to each row (default: true)
            // Also auto-calculate sale prices if they're empty but cost exists
            previewData.value = response.data.preview.map(row => {
                const enrichedRow = {
                    ...row,
                    _selected: true,
                }

                // Normalize all type fields to remove currency symbols
                typeFields.forEach(typeField => {
                    if (enrichedRow[typeField]) {
                        enrichedRow[typeField] = formatNumericValue(enrichedRow[typeField])
                    }
                })

                // Auto-calculate sale prices for all cost/sale field pairs
                costFields.forEach(costField => {
                    const saleField = getSaleFieldName(costField)
                    const cost = enrichedRow[costField]
                    const sale = enrichedRow[saleField]

                    // If cost exists but sale is empty, calculate suggested price
                    if (!isValueEmpty(cost) && isValueEmpty(sale)) {
                        // Get currency for this type
                        const currencyField = getCurrencyFieldName(costField)
                        const currency = enrichedRow[currencyField] || enrichedRow.moeda || 'BRL'

                        // Calculate suggested price
                        const suggestedPrice = computeSuggestedPrice(cost, currency)

                        // Format the suggested price
                        if (suggestedPrice > 0) {
                            enrichedRow[saleField] = formatNumericValue(suggestedPrice)
                        }
                    }
                })


                typeFields.forEach(typeField => {
                    const currencyField = getCurrencyFieldName(typeField)

                    if (!enrichedRow.hasOwnProperty(currencyField) || enrichedRow[currencyField] === null || enrichedRow[currencyField] === undefined) {
                        // Don't set it - leave undefined so backend uses moeda column
                    }
                })

                return enrichedRow
            })
            filePath.value = response.data.file_path
            uploadedFile.value = file
            sellers.value = response.data.sellers || []

            // Extract all columns from the first row, excluding internal fields and currency fields
            // Currency fields will be displayed inline with their corresponding price fields
            if (response.data.preview && response.data.preview.length > 0) {
                const firstRow = response.data.preview[0]
                const allColumns = Object.keys(firstRow).filter(key =>
                    !key.startsWith('_') &&
                    !['row_number', 'status', 'existing_id'].includes(key) &&
                    !isCurrencyField(key) && // Exclude currency fields - they'll be shown inline
                    !key.endsWith('_coin') && // Exclude _coin fields
                    !shouldHideColumn(key)
                )

                // // Sort to put display columns first if they exist
                // const displayColumns = allColumns.filter(col =>
                //     col === 'custo_de_exibicao' || col === 'venda_de_exibicao'
                // )
                // const otherColumns = allColumns.filter(col =>
                //     col !== 'custo_de_exibicao' && col !== 'venda_de_exibicao'
                // )

                previewColumns.value = [...allColumns]
            }
        } else {
            proxy.$toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: response.data.error || $t('Failed to preview file'),
                life: 5000
            })
        }
    } catch (err) {
        console.error('Error previewing file:', err)
        console.error('Error response:', err.response?.data)

        const errorMessage = err.response?.data?.message ||
                           err.response?.data?.error ||
                           err.message ||
                           $t('Failed to preview file')

        proxy.$toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: errorMessage,
            life: 10000
        })
    } finally {
        isLoadingPreview.value = false
        hidePageLoading()
    }
}

// Function to update all currencies when moeda changes
const updateAllCurrenciesFromMoeda = (data) => {
    if (!data.moeda) return

    const typeFields = ['c_cassino', 'v_cassino', 'c_bets', 'v_bets', 'c_geral', 'v_geral',
                       'c_cbd', 'v_cbd', 'c_crip', 'v_crip', 'c_dating', 'v_dating',
                       'c_erotic', 'v_erotic', 'c_loan', 'v_loan', 'c_forex', 'v_forex']

    typeFields.forEach(field => {
        const currencyField = getCurrencyFieldName(field)
        if (data[field] !== null && data[field] !== undefined && data[field] !== '') {
            data[currencyField] = data.moeda
        }
    })
}

// Handle cell edit completion
const onCellEditComplete = (event) => {
    let { data, newValue, field } = event

    data[field] = newValue

    if (field === 'moeda') {
        updateAllCurrenciesFromMoeda(data)
        return
    }

    if (false && field === 'moeda') {
        costFields.forEach(costField => {
            const cost = data[costField]

            if (!isValueEmpty(cost)) {
                const currencyFieldName = getCurrencyFieldName(costField)
                const hasSpecificCurrency = data[currencyFieldName] && data[currencyFieldName] !== null && data[currencyFieldName] !== undefined

                if (!hasSpecificCurrency) {
                    const saleField = getSaleFieldName(costField)
                    const currency = newValue || 'BRL'

                    const suggestedPrice = computeSuggestedPrice(cost, currency)

                    if (suggestedPrice > 0) {
                        data[saleField] = formatNumericValue(suggestedPrice)
                    }
                }
            }
        })
    }

    if (isCostField(field)) {
        const costValue = newValue
        const costCoinField = getCurrencyFieldName(field)
        const costCoin = data[costCoinField] || data.moeda || 'BRL'

        if (!isValueEmpty(costValue)) {
            const currency = costCoin
            const saleField = getSaleFieldName(field)
            const suggestedPrice = computeSuggestedPrice(costValue, currency)

            if (suggestedPrice > 0) {
                data[saleField] = formatNumericValue(suggestedPrice)
            }
        } else if (isValueEmpty(costValue)) {
            const saleField = getSaleFieldName(field)
            data[saleField] = ''
        }
    }

    if (isTypeField(field)) {
        const currencyFieldName = getCurrencyFieldName(field)
        if (!data[currencyFieldName]) {
            data[currencyFieldName] = data.moeda || 'BRL'
        }
    }

    if (isAnchorField(field)) {
        data[field] = validateAnchorValue(newValue)
    }

    if (field === 'seller') {
        updateExistingValuesForSeller(data, newValue)
        // Force Vue reactivity by updating the reference
        if (previewData.value && data.row_number) {
            const rowIndex = previewData.value.findIndex(row => row.row_number === data.row_number)
            if (rowIndex !== -1) {
                // Create a new object reference to trigger reactivity
                previewData.value[rowIndex] = { ...previewData.value[rowIndex] }
            }
        }
    }
}

// Helper to ensure currency field exists and return its value
// Only initializes when user opens the editor for the first time
const ensureCurrencyField = (data, field) => {
    const currencyFieldName = getCurrencyFieldName(field)

    // If currency field exists and has a value, return it (user already set it)
    if (data[currencyFieldName] && data[currencyFieldName] !== null && data[currencyFieldName] !== undefined) {
        return data[currencyFieldName]
    }

    // Field doesn't exist or is empty - initialize with default currency
    const defaultCurrency = data.moeda || 'BRL'

    // Find the row in previewData and update it to persist the change
    if (previewData.value && data.row_number) {
        const rowIndex = previewData.value.findIndex(row => row.row_number === data.row_number)
        if (rowIndex !== -1) {
            previewData.value[rowIndex][currencyFieldName] = defaultCurrency
        }
    }

    // Also update local reference
    data[currencyFieldName] = defaultCurrency

    return defaultCurrency
}

// Handler for currency change - updates currency without closing editor
// Also recalculates sale price if cost exists and checks if modal should be shown
const onCurrencyChange = (data, field, newCurrency) => {
    const currencyFieldName = getCurrencyFieldName(field)

    // Find the row in previewData by row_number (unique identifier)
    if (previewData.value && data.row_number) {
        const rowIndex = previewData.value.findIndex(row => row.row_number === data.row_number)

        if (rowIndex !== -1) {
            // Update the currency field directly on the found object
            previewData.value[rowIndex][currencyFieldName] = newCurrency

            // Also update the local data reference to ensure immediate UI update
            data[currencyFieldName] = newCurrency

            // If this is a cost field with a value, recalculate the sale price
            if (isCostField(field) && !isValueEmpty(data[field])) {
                const saleField = getSaleFieldName(field)
                const costValue = data[field]

                // Calculate suggested price with new currency
                const suggestedPrice = computeSuggestedPrice(costValue, newCurrency)

                // Format and update the sale field
                if (suggestedPrice > 0) {
                    const formattedPrice = formatNumericValue(suggestedPrice)
                    previewData.value[rowIndex][saleField] = formattedPrice
                    data[saleField] = formattedPrice
                }

            }
        }
    } else {
        // Fallback: update directly if no row_number
        data[currencyFieldName] = newCurrency

        // Recalculate sale if this is a cost field
        if (isCostField(field) && !isValueEmpty(data[field])) {
            const saleField = getSaleFieldName(field)
            const cost = data[field]

            const suggestedPrice = computeSuggestedPrice(cost, newCurrency)

            if (suggestedPrice > 0) {
                data[saleField] = formatNumericValue(suggestedPrice)
            }
        }
    }
}

const rowClass = (data) => {
    if (data._failed) return 'bg-red-50'
    if (data._imported) return 'bg-green-50 opacity-60'
    if (data._notImported) return 'opacity-40'
    return ''
}

const isCellEditable = (data) => {
    return !data._imported
}

// Helper to check if a value is effectively empty (0, '', null, '0,00' etc)
const isValueEmpty = (value) => {
    if (value === null || value === undefined || value === '') return true

    // Remove formatting and check if it's 0
    const cleaned = String(value).replace(/[.,\s]/g, '').trim()
    return cleaned === '' || cleaned === '0' || cleaned === '00' || cleaned === '000'
}

// Helper function to get the display value for comparison
const getComparisonValue = (data, field) => {
    if (data.status !== 'UPDATE' || !data._existing) {
        return {
            hasChange: false,
            oldValue: null,
            newValue: normalizeTypeValue(data[field], field),
            oldCurrency: null,
            newCurrency: null
        }
    }

    // Ensure existing values are up to date with current seller for offer fields
    if ((isTypeField(field)) && data._offers_by_seller) {
        const currentSeller = data.seller
        if (currentSeller) {
            const seller = sellers.value.find(s => s.name === currentSeller)
            if (seller && data._offers_by_seller[seller.id]) {
                const sellerOffers = data._offers_by_seller[seller.id]
                if (sellerOffers && sellerOffers[field] !== undefined && data._existing[field] !== sellerOffers[field]) {
                    // Silently update the existing value to match current seller's offers
                    data._existing[field] = sellerOffers[field]
                }
            }
        }
    }

    // Get raw values
    const rawOldValue = data._existing[field] ?? ''
    const rawNewValue = data[field] ?? ''

    // Normalize for type fields
    const oldValue = normalizeTypeValue(rawOldValue, field)
    const newValue = normalizeTypeValue(rawNewValue, field)

    // Check if both values are empty
    const oldIsEmpty = isValueEmpty(oldValue)
    const newIsEmpty = isValueEmpty(newValue)

    if (isSaleField(field)) {
        const sellerName = data.seller || data.responsavel
        const isLinkarme = isSellerLinkarme(sellerName)

        if (!isLinkarme) {
            return {
                hasChange: false,
                oldValue,
                newValue,
                oldCurrency: null,
                newCurrency: null,
                valueChanged: false,
                currencyChanged: false
            }
        }
    }

    // If both old and new values are empty, don't show as change
    // (even if currency is different - currency without value is not meaningful)
    if (oldIsEmpty && newIsEmpty) {
        return {
            hasChange: false,
            oldValue,
            newValue,
            oldCurrency: null,
            newCurrency: null,
            valueChanged: false,
            currencyChanged: false
        }
    }

    // Check for value change (case-insensitive comparison)
    const valueChanged = String(oldValue).trim().toLowerCase() !== String(newValue).trim().toLowerCase()

    // For type fields, also check if currency changed (only if there's a value)
    let currencyChanged = false
    let oldCurrency = null
    let newCurrency = null

    if (isTypeField(field) && (!oldIsEmpty || !newIsEmpty)) {
        // For type fields, use getCurrencyFieldName helper
        const currencyFieldName = getCurrencyFieldName(field)

        oldCurrency = data._existing[currencyFieldName] ?? data._existing.moeda ?? 'BRL'
        newCurrency = data[currencyFieldName] ?? data.moeda ?? 'BRL'

        // Only consider currency changed if there's actually a value to apply it to
        currencyChanged = String(oldCurrency).trim().toUpperCase() !== String(newCurrency).trim().toUpperCase()
    }

    // Has change if either value or currency changed (and there's an actual value)
    const hasChange = valueChanged || currencyChanged

    return {
        hasChange,
        oldValue,
        newValue,
        oldCurrency,
        newCurrency,
        valueChanged,
        currencyChanged
    }
}

const confirmImport = () => {
    if (!filePath.value && !previewData.value) return

    // Filter only selected rows
    const selectedRows = previewData.value.filter(row => row._selected)

    if (selectedRows.length === 0) {
        proxy.$toast.add({
            severity: 'warn',
            summary: $t('No Sites Selected'),
            detail: $t('Please select at least one site to import'),
            life: 5000
        })
        return
    }

    checkForNewSellers(selectedRows)

}

const checkForNewSellers = (selectedRows) => {
    const sellersInData = new Set()
    selectedRows.forEach(row => {
        const sellerName = row.seller || row.responsavel
        if (sellerName && sellerName.trim()) {
            sellersInData.add(sellerName.trim())
        }
    })

    const existingSellerNames = new Set(sellers.value.map(s => s.name))
    const existingFound = []
    const newFound = []

    Array.from(sellersInData).forEach(seller => {
        if (existingSellerNames.has(seller)) {
            existingFound.push(seller)
        } else {
            newFound.push(seller)
        }
    })

    if (newFound.length > 0) {
        newSellers.value = newFound
        existingSellers.value = existingFound
        newSellersEmails.value = {}
        newFound.forEach(seller => {
            newSellersEmails.value[seller] = ''
        })
        showNewSellersModal.value = true
    } else {
        proceedWithImport(selectedRows)
    }
}

const proceedWithImport = (selectedRows = null) => {
    if (!selectedRows) {
        selectedRows = previewData.value.filter(row => row._selected)
    }

    importProgressTotal.value = selectedRows.length
    importProgressCurrent.value = 0
    importProgressMessage.value = $t('Starting import...')
    showImportProgressModal.value = true

    // Clean up currency fields that are undefined/null to preserve moeda column behavior
    // Only send currency fields that were explicitly set by the user
    const cleanedRows = selectedRows.map(row => {
        const cleanedRow = { ...row }

        // Remove undefined/null currency fields to let backend use 'moeda' as fallback
        typeCurrencyFields.forEach(currencyField => {
            if (cleanedRow[currencyField] === null || cleanedRow[currencyField] === undefined || cleanedRow[currencyField] === '') {
                delete cleanedRow[currencyField]
            }
        })

        // Ensure cost/sale fields are sent as empty strings if empty/undefined/null
        // This is critical for soft-delete functionality
        const costSaleFields = ['c_cassino', 'v_cassino', 'c_cbd', 'v_cbd', 'c_crip', 'v_crip', 'c_loan', 'v_loan',
                                 'c_forex', 'v_forex', 'c_dating', 'v_dating', 'c_erotic', 'v_erotic', 'c_bets', 'v_bets',
                                 'c_geral', 'v_geral', 'custo_de_exibicao', 'venda_de_exibicao']

        costSaleFields.forEach(field => {
            if (cleanedRow[field] === null || cleanedRow[field] === undefined) {
                cleanedRow[field] = ''
            }
        })

        // If user provided accepted_anchors in CSV, use it for all undefined anchor fields
        if (cleanedRow.accepted_anchors && !isValueEmpty(cleanedRow.accepted_anchors)) {
            const globalAnchorValue = parseInt(cleanedRow.accepted_anchors, 10)

            if (globalAnchorValue >= 1 && globalAnchorValue <= 3) {
                const anchorFields = ['c_cassino_anchors', 'c_cbd_anchors', 'c_loan_anchors', 'c_forex_anchors',
                                      'c_crip_anchors', 'c_dating_anchors', 'c_erotic_anchors', 'c_bets_anchors', 'c_geral_anchors']

                anchorFields.forEach(field => {
                    if (cleanedRow[field] === null || cleanedRow[field] === undefined || cleanedRow[field] === '') {
                        cleanedRow[field] = globalAnchorValue
                    }
                })
            }
        }

        // Ensure anchors fields are sent as integers (default to 1 if null/undefined)
        const anchorFields = ['c_cassino_anchors', 'c_cbd_anchors', 'c_loan_anchors', 'c_forex_anchors',
                              'c_crip_anchors', 'c_dating_anchors', 'c_erotic_anchors', 'c_bets_anchors', 'c_geral_anchors']

        anchorFields.forEach(field => {
            if (cleanedRow[field] === null || cleanedRow[field] === undefined || cleanedRow[field] === '') {
                cleanedRow[field] = 1  // Minimum accepted anchors is 1
            } else {
                // Ensure it's an integer
                cleanedRow[field] = parseInt(cleanedRow[field], 10) || 1
            }
        })

        if (cleanedRow.link_type === 'SPONSORED') {
            cleanedRow.link_type = 'SPONSOR'
        }

        return cleanedRow
    })

    // Start the actual import
    const importData = {
        file_path: filePath.value,
        preview_data: cleanedRows, // Send cleaned rows
        notify_added: notifyAdded.value,
        notify_updated: notifyUpdated.value,
        new_sellers: newSellersEmails.value,
        ignore_empty_site_fields: ignoreEmptySiteFields.value,
        import_pricing_only: importPricingOnly.value,
    }

    router.post(proxy.route ? proxy.route('sites.import') : '/admin/sites/import', importData, {
        preserveScroll: true,
        preserveState: true,
        onBefore: () => {
            // Prevent default Inertia loading
            hidePageLoading()
            importProgressMessage.value = $t('Import in progress, please wait...')
            return true
        },
        onStart: () => {
            // Ensure no default loading is shown
            hidePageLoading()
            importProgressMessage.value = $t(`Processing ${importProgressTotal.value} sites. This may take several minutes...`)
        },
        onError(err) {
            showImportProgressModal.value = false
        },
        onSuccess(page) {
            importProgressCurrent.value = importProgressTotal.value
            importProgressMessage.value = $t('Import completed!')

            setTimeout(() => {
                showImportProgressModal.value = false
            }, 1500)

            importSummary.value = {
                added: page.props.importAdded || [],
                updated: page.props.importUpdated || [],
                failures: page.props.importFailures || [],
                errors: page.props.importErrors || [],
                newSellers: page.props.newSellersCreated || [],
                totalProcessed: importProgressTotal.value
            }

            // Check if there are failures
            if (page.props.importFailures && page.props.importFailures.length > 0) {
                // Keep preview data and mark failed rows
                markFailedRows(page.props.importFailures)
                importFinished.value = false

                // Count successful rows
                const successCount = previewData.value.filter(d => d._success).length
                const failedCount = page.props.importFailures.length

                proxy.$toast.add({
                    severity: 'warn',
                    summary: $t('Import Completed with Errors'),
                    detail: $t('Some rows failed. Please fix the errors and try again.', {
                        success: successCount,
                        failed: failedCount
                    }),
                    life: 8000
                })
            } else {
                // Success - clear preview
                previewData.value = null
                filePath.value = null
                importFinished.value = true

                proxy.$toast.add({
                    severity: 'success',
                    summary: $t('Import Successful'),
                    detail: $t('All rows imported successfully!', { count: importSummary.value.totalProcessed }),
                    life: 5000
                })
            }
        },
        onFinish: () => {
            // Ensure loading is hidden when request completes
            hidePageLoading()
        }
    })
}

const markFailedRows = (failures) => {
    if (!failures || failures.length === 0) return
    if (!previewData.value) return

    // Create a map of failed row numbers and their errors
    const failureMap = new Map()
    failures.forEach(failure => {
        failureMap.set(failure.row, failure.errors)
    })

    const hasImportFailures = failures.length > 0

    // Mark failed and successful rows
    previewData.value = previewData.value.map(row => {
        if (hasImportFailures && !row._selected) {
            return {
                ...row,
                _notImported: true
            }
        }
        if (failureMap.has(row.row_number)) {
            return {
                ...row,
                _failed: true,
                _success: false, // Ensure success flag is removed
                _imported: false,
                _errors: failureMap.get(row.row_number)
            }
        } else if (row._selected) {
            return {
                ...row,
                _success: true,
                _imported: true,
                _failed: false
            }
        } else {
            return {
                ...row,
                _success: false,
                _failed: false
            }
        }
    })

    // If all rows succeeded, clear preview
    const hasFailures = previewData.value.some(row => row._failed)
    if (!hasFailures) {
        previewData.value = null
        filePath.value = null
        importFinished.value = true
    }
}

const resetImport = () => {
    notifyAdded.value = false
    notifyUpdated.value = false
    importFinished.value = false
    uploadedFile.value = null
    previewData.value = null
    filePath.value = null
    previewColumns.value = []
    importSummary.value = null
    newSellers.value = []
    existingSellers.value = []
    newSellersEmails.value = {}

    router.reload({
        only: ['importFailures', 'importErrors', 'importAdded', 'importUpdated', 'newSellersCreated'],
    })
}

const cancelPreview = () => {
    previewData.value = null
    filePath.value = null
    uploadedFile.value = null
    previewColumns.value = []
    if (importFilepond.value) {
        importFilepond.value.clear()
    }
}

const goBack = () => {
    router.visit(proxy.route ? proxy.route('sites.index') : '/admin/sites', {
        onStart: () => showPageLoading($t('Loading...')),
        onFinish: () => hidePageLoading(),
        onError: () => hidePageLoading()
    });
}

// Select All functionality
const allSelected = computed({
    get: () => {
        if (!previewData.value || previewData.value.length === 0) return false
        return previewData.value.every(row => row._selected)
    },
    set: (value) => {
        if (previewData.value) {
            previewData.value.forEach(row => {
                row._selected = value
            })
        }
    }
})

const updateExistingValuesForSeller = (data, newSellerName) => {
    if (!data._offers_by_seller || data.status !== 'UPDATE') return

    const seller = sellers.value.find(s => s.name === newSellerName)
    if (!seller) {
        clearExistingOfferValues(data)
        return
    }

    const sellerOffers = data._offers_by_seller[seller.id]

    if (!sellerOffers) {
        clearExistingOfferValues(data)
        return
    }

    clearExistingOfferValues(data)

    let updatedFields = 0
    Object.keys(sellerOffers).forEach(key => {
        if (data._existing && sellerOffers[key] !== null && sellerOffers[key] !== undefined) {
            data._existing[key] = sellerOffers[key]
            data[key] = sellerOffers[key]
            updatedFields++
        }
    })

    // Calculate and update moeda based on the new seller's offers currencies
    const currencies = new Set()
    Object.keys(sellerOffers).forEach(key => {
        if (key.endsWith('_coin')) {
            const currency = sellerOffers[key]
            if (currency && currency !== null && currency !== undefined) {
                currencies.add(currency)
            }
        }
    })

    // Determine moeda: if all currencies are the same, use that; otherwise use fallback
    let newMoeda = 'BRL' // Default fallback
    if (currencies.size === 1) {
        newMoeda = Array.from(currencies)[0]
    }

    // Update moeda in _existing and root level
    if (data._existing) {
        data._existing.moeda = newMoeda
    }
    data.moeda = newMoeda
}

const runInBackground = () => {
    showImportProgressModal.value = false

    proxy.$toast.add({
        severity: 'info',
        summary: $t('Import Running in Background'),
        detail: $t('The import will continue processing.'),
        life: 5000
    })
}

const validateNewSellersEmails = () => {
    for (const seller of newSellers.value) {
        const email = newSellersEmails.value[seller]
        if (email && email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(email.trim())) {
                proxy.$toast.add({
                    severity: 'error',
                    summary: $t('Invalid Email'),
                    detail: 'Please provide a valid email for seller: ' + seller,
                    life: 5000
                })
                return false
            }
        }
    }
    return true
}

const confirmNewSellers = () => {
    if (!validateNewSellersEmails()) {
        return
    }

    showNewSellersModal.value = false
    const selectedRows = previewData.value.filter(row => row._selected)
    proceedWithImport(selectedRows)
}

const cancelNewSellers = () => {
    showNewSellersModal.value = false
    newSellers.value = []
    existingSellers.value = []
    newSellersEmails.value = {}
}

// Helper function to clear all offer-related existing values
const clearExistingOfferValues = (data) => {
    if (!data._existing) return

    const typeFields = ['c_cassino', 'v_cassino', 'c_bets', 'v_bets', 'c_geral', 'v_geral',
                       'c_cbd', 'v_cbd', 'c_crip', 'v_crip', 'c_dating', 'v_dating',
                       'c_erotic', 'v_erotic', 'c_loan', 'v_loan', 'c_forex', 'v_forex']

    typeFields.forEach(field => {
        data._existing[field] = ''
        data._existing[field + '_coin'] = 'BRL'
        if (field.startsWith('c_')) {
            data._existing[field + '_anchors'] = 0
        }
    })

    data._existing.moeda = 'BRL'
}
</script>

<template>
    <!-- Main Content -->
    <div class="w-full px-2 sm:px-4 lg:px-0">
        <div class="flex flex-col gap-4 max-w-full">
            <!-- Header -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                <div>
                    <h1 class="text-xl font-bold text-surface-900">{{ $t('Import Sites') }}</h1>
                    <p class="text-sm text-surface-600 mt-1">{{ $t('Upload a CSV or Excel file to import sites') }}</p>
                </div>
                <Button
                    :label="$t('Back to Sites')"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    outlined
                    size="small"
                    @click="goBack"
                    class="w-full sm:w-auto"
                />
            </div>

            <!-- Import Card -->
            <Card class="overflow-hidden">
                <template #content>
                    <div class="flex flex-col gap-4 min-w-0">
                        <!-- File Upload -->
                        <div v-if="!importFinished && !previewData" class="flex flex-col gap-4">
                            <div>
                                <label class="block text-sm font-semibold text-surface-700 mb-2">{{ $t('Upload File') }}</label>
                                <FileUpload
                                    ref="importFilepond"
                                    mode="basic"
                                    name="file"
                                    accept=".csv,.xlsx,.xls"
                                    :maxFileSize="5000000"
                                    :auto="true"
                                    :disabled="isLoadingPreview"
                                    :choose-label="isLoadingPreview ? $t('Loading...') : $t('Choose CSV or Excel File')"
                                    @select="uploadSites"
                                />
                                <p class="text-sm text-surface-500 mt-2">{{ $t('Supported formats: CSV, Excel (.xlsx, .xls). Maximum file size: 5MB') }}</p>

                                <!-- Import Mode Toggle -->
                                <div class="mt-4 p-3 bg-surface-50 rounded-lg border border-surface-200">
                                    <label class="block text-sm font-semibold text-surface-700 mb-2">{{ $t('Import Mode') }}</label>
                                    <div class="flex items-center gap-3">
                                        <span :class="['text-sm', !importPricingOnly ? 'font-semibold text-primary-600' : 'text-surface-500']">
                                            {{ $t('Import Site Data') }}
                                        </span>
                                        <InputSwitch v-model="importPricingOnly" />
                                        <span :class="['text-sm', importPricingOnly ? 'font-semibold text-primary-600' : 'text-surface-500']">
                                            {{ $t('Import Pricing Only') }}
                                        </span>
                                    </div>
                                    <p class="text-xs text-surface-500 mt-2">
                                        {{ importPricingOnly
                                            ? $t('Only update prices for existing sites. Site data columns will be hidden.')
                                            : $t('Update all site data including prices.')
                                        }}
                                    </p>
                                </div>

                                <!-- Checkbox to ignore empty fields - Only shown when importing site data -->
                                <div v-if="!importPricingOnly" class="flex items-center mt-3">
                                    <Checkbox
                                        v-model="ignoreEmptySiteFields"
                                        :binary="true"
                                        inputId="ignoreEmptySiteFields"
                                    />
                                    <label for="ignoreEmptySiteFields" class="ml-2 text-sm text-surface-700 cursor-pointer">
                                        {{ $t('Ignore empty site data fields during import') }}
                                    </label>
                                </div>
                                <p v-if="!importPricingOnly" class="text-xs text-surface-500 mt-1 ml-6">
                                    {{ $t('When checked, empty values in the import file will not overwrite existing data.') }}
                                </p>
                            </div>

                            <!-- Info Box -->
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div class="flex gap-3">
                                    <i class="pi pi-info-circle text-blue-600 mt-0.5"></i>
                                    <div class="text-sm text-blue-900">
                                        <div class="font-semibold mb-2">{{ $t('Import Information') }}</div>
                                        <ul class="list-disc list-inside space-y-1 text-blue-800">
                                            <li>{{ $t('The file should contain site information with proper headers') }}</li>
                                            <li>{{ $t('Existing sites will be updated based on URL matching') }}</li>
                                            <li>{{ $t('New sites will be created with PENDING status') }}</li>
                                            <li>{{ $t('Processing may take a few moments for large files') }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Preview Table -->
                        <div v-if="previewData && !importFinished" class="flex flex-col gap-4">
                            <div>
                                <h3 class="text-lg font-semibold text-surface-900">
                                    {{ previewData.some(d => d._failed) ? $t('Fix Failed Rows') : $t('Preview Import Data') }}
                                </h3>
                                <p class="text-sm text-surface-600 mt-1">
                                    {{ previewData.some(d => d._failed) ? $t('Edit the failed rows below and retry import') : $t('Review the data below before importing') }}
                                </p>
                            </div>

                            <!-- Import Settings Summary -->
                            <div class="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm">
                                <div class="flex items-center gap-2">
                                    <i :class="['pi', importPricingOnly ? 'pi-dollar text-blue-600' : 'pi-database text-blue-600']"></i>
                                    <span class="text-surface-600">{{ $t('Mode') }}:</span>
                                    <span :class="importPricingOnly ? 'font-semibold text-blue-700' : 'font-semibold text-blue-700'">
                                        {{ importPricingOnly ? $t('Import Pricing Only') : $t('Import Site Data') }}
                                    </span>
                                </div>
                                <span v-if="!importPricingOnly" class="text-surface-300">|</span>
                                <div v-if="!importPricingOnly" class="flex items-center gap-2">
                                    <i :class="['pi', ignoreEmptySiteFields ? 'pi-check-circle text-green-600' : 'pi-circle text-surface-400']"></i>
                                    <span class="text-surface-600">{{ $t('Ignore empty site data fields during import') }}:</span>
                                    <span :class="ignoreEmptySiteFields ? 'font-semibold text-green-700' : 'font-semibold text-surface-500'">
                                        {{ ignoreEmptySiteFields ? $t('Yes') : $t('No') }}
                                    </span>
                                </div>
                            </div>

                            <!-- Statistics - Clickable Filters -->
                            <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <!-- Total (First) -->
                                <div
                                    @click="activeStatusFilter = 'ALL'"
                                    :class="[
                                        'rounded-lg p-4 cursor-pointer transition-all',
                                        activeStatusFilter === 'ALL'
                                            ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                            : 'bg-white border border-surface-300 hover:border-blue-400'
                                    ]"
                                >
                                    <div :class="[
                                        'text-2xl font-bold mb-1',
                                        activeStatusFilter === 'ALL' ? 'text-blue-700' : 'text-surface-700'
                                    ]">{{ selectedCounts.total }}/{{ totalCounts.total }}</div>
                                    <div :class="[
                                        'text-sm',
                                        activeStatusFilter === 'ALL' ? 'text-blue-600' : 'text-surface-600'
                                    ]">{{ $t('Total imports') }}</div>
                                </div>

                                <!-- New Sites -->
                                <div
                                    @click="activeStatusFilter = 'NEW'"
                                    :class="[
                                        'rounded-lg p-4 cursor-pointer transition-all',
                                        activeStatusFilter === 'NEW'
                                            ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                            : 'bg-white border border-surface-300 hover:border-blue-400'
                                    ]"
                                >
                                    <div :class="[
                                        'text-2xl font-bold mb-1',
                                        activeStatusFilter === 'NEW' ? 'text-blue-700' : 'text-surface-700'
                                    ]">{{ selectedCounts.new }}/{{ totalCounts.new }}</div>
                                    <div :class="[
                                        'text-sm',
                                        activeStatusFilter === 'NEW' ? 'text-blue-600' : 'text-surface-600'
                                    ]">{{ $t('New Sites') }}</div>
                                </div>

                                <!-- Updates -->
                                <div
                                    @click="activeStatusFilter = 'UPDATE'"
                                    :class="[
                                        'rounded-lg p-4 cursor-pointer transition-all',
                                        activeStatusFilter === 'UPDATE'
                                            ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                            : 'bg-white border border-surface-300 hover:border-blue-400'
                                    ]"
                                >
                                    <div :class="[
                                        'text-2xl font-bold mb-1',
                                        activeStatusFilter === 'UPDATE' ? 'text-blue-700' : 'text-surface-700'
                                    ]">{{ selectedCounts.update }}/{{ totalCounts.update }}</div>
                                    <div :class="[
                                        'text-sm',
                                        activeStatusFilter === 'UPDATE' ? 'text-blue-600' : 'text-surface-600'
                                    ]">{{ $t('Updates') }}</div>
                                </div>

                                <!-- Failed Rows (if any) -->
                                <div
                                    v-if="totalCounts.failed > 0"
                                    @click="activeStatusFilter = 'FAILED'"
                                    :class="[
                                        'rounded-lg p-4 cursor-pointer transition-all',
                                        activeStatusFilter === 'FAILED'
                                            ? 'bg-blue-100 border-2 border-blue-500 shadow-md'
                                            : 'bg-white border border-surface-300 hover:border-blue-400'
                                    ]"
                                >
                                    <div :class="[
                                        'text-2xl font-bold mb-1',
                                        activeStatusFilter === 'FAILED' ? 'text-blue-700' : 'text-surface-700'
                                    ]">{{ selectedCounts.failed }}/{{ totalCounts.failed }}</div>
                                    <div :class="[
                                        'text-sm',
                                        activeStatusFilter === 'FAILED' ? 'text-blue-600' : 'text-surface-600'
                                    ]">{{ $t('Failed Rows') }}</div>
                                </div>
                            </div>

                            <!-- Data Table -->
                            <div class="w-full">
                                <DataTable
                                    :value="filteredPreviewData"
                                    dataKey="row_number"
                                    :paginator="filteredPreviewData.length > 10"
                                    :rows="10"
                                    :rowsPerPageOptions="[10, 25, 50]"
                                    scrollable
                                    scrollHeight="500px"
                                    class="text-sm border border-surface-200 rounded-lg"
                                    editMode="cell"
                                    @cell-edit-complete="onCellEditComplete"
                                    :rowClass="rowClass"
                                >
                                    <!-- Checkbox Column -->
                                    <Column
                                        frozen
                                        style="min-width: 60px; max-width: 60px;"
                                    >
                                        <template #header>
                                            <Checkbox
                                                v-model="allSelected"
                                                :binary="true"
                                            />
                                        </template>
                                        <template #body="slotProps">
                                            <Checkbox
                                                v-model="slotProps.data._selected"
                                                :binary="true"
                                                :disabled="slotProps.data._notImported"
                                            />
                                        </template>
                                    </Column>

                                    <!-- Row Number Column -->
                                    <Column
                                        field="row_number"
                                        :header="$t('Row')"
                                        frozen
                                        style="min-width: 80px"
                                    ></Column>

                                    <!-- Status Column -->
                                    <Column
                                        field="status"
                                        :header="$t('Status')"
                                        frozen
                                        style="min-width: 140px"
                                    >
                                        <template #body="slotProps">
                                            <div class="flex items-center gap-2">
                                                <Tag
                                                    v-if="slotProps.data._failed"
                                                    value="FAILED"
                                                    severity="danger"
                                                />
                                                <Tag
                                                    v-else-if="slotProps.data._success"
                                                    value="SUCCESS"
                                                    severity="success"
                                                />
                                                <Tag
                                                    v-else-if="slotProps.data._notImported"
                                                    value="NOT IMPORTED"
                                                    severity="secondary"
                                                />
                                                <Tag
                                                    v-else-if="shouldShowStatus(slotProps.data)"
                                                    :value="slotProps.data.status"
                                                    :severity="slotProps.data.status === 'NEW' ? 'info' : 'warn'"
                                                />
                                                <i
                                                    v-if="slotProps.data._failed && slotProps.data._errors"
                                                    class="pi pi-info-circle text-red-500 cursor-help"
                                                    v-tooltip.right="slotProps.data._errors.join(', ')"
                                                ></i>
                                            </div>
                                        </template>
                                    </Column>

                                    <!-- Dynamic Columns for all CSV fields (filtered by import mode) -->
                                    <Column
                                        v-for="col in visibleColumns"
                                        v-if="!shouldHideColumn(col)"
                                        :key="col"
                                        :field="col"
                                        :header="col.charAt(0).toUpperCase() + col.slice(1).replace(/_/g, ' ')"
                                        style="min-width: 200px"
                                    >
                                        <template #body="slotProps">
                                            <!-- Display fields: custo_de_exibicao and venda_de_exibicao -->
                                            <div v-if="col === 'custo_de_exibicao' || col === 'venda_de_exibicao'">
                                                <div v-if="getComparisonValue(slotProps.data, col).hasChange" class="flex flex-col gap-1">
                                                    <!-- Old Value with Currency -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-red-600 font-medium">Old:</span>
                                                        <span class="line-through text-red-700 bg-red-50 px-2 py-0.5 rounded">
                                                            {{ formatValueWithCurrency(
                                                                getComparisonValue(slotProps.data, col).oldValue,
                                                                getComparisonValue(slotProps.data, col).oldCurrency
                                                            ) || '(empty)' }}
                                                        </span>
                                                    </div>
                                                    <!-- New Value with Currency -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-green-600 font-medium">New:</span>
                                                        <span :class="['font-semibold bg-green-50 px-2 py-0.5 rounded flex items-center gap-1', col === 'custo_de_exibicao' ? 'text-blue-700' : 'text-green-700']">
                                                            {{ formatValueWithCurrency(
                                                                getComparisonValue(slotProps.data, col).newValue,
                                                                getComparisonValue(slotProps.data, col).newCurrency
                                                            ) || '(empty)' }}
                                                            <i v-if="slotProps.data[col === 'custo_de_exibicao' ? '_displayCostManuallyEdited' : '_displaySaleManuallyEdited']"
                                                               class="pi pi-pencil text-xs text-orange-500"
                                                               v-tooltip="$t('Editado manualmente')"
                                                            ></i>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div v-else class="flex items-center gap-1">
                                                    <span :class="['font-semibold', col === 'custo_de_exibicao' ? 'text-blue-700' : 'text-green-700']">
                                                        {{ formatValueWithCurrency(
                                                            slotProps.data[col],
                                                            slotProps.data[col + '_coin'] || slotProps.data.moeda || 'BRL'
                                                        ) }}
                                                    </span>
                                                    <i v-if="slotProps.data[col === 'custo_de_exibicao' ? '_displayCostManuallyEdited' : '_displaySaleManuallyEdited']"
                                                       class="pi pi-pencil text-xs text-orange-500"
                                                       v-tooltip="$t('Editado manualmente')"
                                                    ></i>
                                                </div>
                                            </div>

                                            <!-- Type fields: show value with currency -->
                                            <div v-else-if="isTypeField(col)">
                                                <div v-if="getComparisonValue(slotProps.data, col).hasChange" class="flex flex-col gap-1">
                                                    <!-- Old Value with Currency -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-red-600 font-medium">Old:</span>
                                                        <span class="line-through text-red-700 bg-red-50 px-2 py-0.5 rounded">
                                                            {{ formatValueWithCurrency(
                                                                getComparisonValue(slotProps.data, col).oldValue,
                                                                getComparisonValue(slotProps.data, col).oldCurrency
                                                            ) || '(empty)' }}
                                                        </span>
                                                    </div>
                                                    <!-- New Value with Currency -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-green-600 font-medium">New:</span>
                                                        <span class="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                                                            {{ formatValueWithCurrency(
                                                                getComparisonValue(slotProps.data, col).newValue,
                                                                getComparisonValue(slotProps.data, col).newCurrency
                                                            ) || '(empty)' }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span v-else class="break-words">
                                                    <div v-if="isSaleField(col) && !isSellerLinkarme(slotProps.data.seller || slotProps.data.responsavel) && !isValueEmpty(slotProps.data[col])"
                                                         class="inline-flex items-baseline">
                                                        <span class="font-semibold text-green-700">
                                                            {{ formatValueWithCurrency(
                                                                slotProps.data[col],
                                                                slotProps.data[getCurrencyFieldName(col)] || slotProps.data.moeda || 'BRL'
                                                            ) }}
                                                        </span>
                                                        <sup class="text-orange-500 cursor-help text-xs ml-0.5"
                                                             v-tooltip="$t('Valor sugerido')">*</sup>
                                                    </div>
                                                    <span v-else>
                                                        {{ formatValueWithCurrency(
                                                            slotProps.data[col],
                                                            slotProps.data[getCurrencyFieldName(col)] || slotProps.data.moeda || 'BRL'
                                                        ) }}
                                                    </span>
                                                </span>
                                            </div>

                                            <!-- Seller field: special select -->
                                            <div v-else-if="col === 'seller'">
                                                <div>{{ slotProps.data[col] || 'N/A' }}</div>
                                            </div>

                                            <!-- Non-type fields: normal display -->
                                            <div v-else>
                                                <div v-if="getComparisonValue(slotProps.data, col).hasChange" class="flex flex-col gap-1">
                                                    <!-- Old Value -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-red-600 font-medium">Old:</span>
                                                        <span class="line-through text-red-700 bg-red-50 px-2 py-0.5 rounded">
                                                            {{ getComparisonValue(slotProps.data, col).oldValue || '(empty)' }}
                                                        </span>
                                                    </div>
                                                    <!-- New Value -->
                                                    <div class="flex items-center gap-2 text-xs">
                                                        <span class="text-green-600 font-medium">New:</span>
                                                        <span class="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                                                            {{ getComparisonValue(slotProps.data, col).newValue || '(empty)' }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span v-else class="break-words">{{ slotProps.data[col] }}</span>
                                            </div>
                                        </template>
                                        <template #editor="{ data, field }">
                                            <!-- Seller field: select -->
                                            <Select
                                                v-if="field === 'seller'"
                                                v-model="data[field]"
                                                :options="sellers"
                                                optionLabel="name"
                                                optionValue="name"
                                                placeholder="Select seller"
                                                @click.stop
                                                @change="(event) => {
                                                    data[field] = event.value
                                                    updateExistingValuesForSeller(data, event.value)
                                                    // Force reactivity by updating the row reference
                                                    if (previewData && data.row_number) {
                                                        const rowIndex = previewData.findIndex(row => row.row_number === data.row_number)
                                                        if (rowIndex !== -1) {
                                                            previewData[rowIndex] = { ...previewData[rowIndex] }
                                                        }
                                                    }
                                                }"
                                            />

                                            <!-- Display fields: input + currency select -->
                                            <div
                                                v-else-if="isCellEditable(data) && (field === 'custo_de_exibicao' || field === 'venda_de_exibicao')"
                                                class="flex gap-1 items-center"
                                                @mousedown.stop
                                                @click.stop
                                            >
                                                <InputText
                                                    v-model="data[field]"
                                                    class="flex-1"
                                                    placeholder="0,00"
                                                    @click.stop
                                                />
                                                <span class="w-30 flex-shrink-0 text-sm font-medium text-gray-600 px-2 py-1 bg-gray-100 rounded">
                                                    {{ data.moeda || 'BRL' }}
                                                </span>
                                            </div>

                                            <!-- Type fields: input + currency select -->
                                            <div
                                                v-else-if="isCellEditable(data) && isTypeField(field)"
                                                class="flex gap-1 items-center"
                                                @mousedown.stop
                                                @click.stop
                                            >
                                                <InputText
                                                    v-model="data[field]"
                                                    class="flex-1"
                                                    placeholder="0,00"
                                                    :disabled="isSaleFieldReadOnly(data, field)"
                                                    :class="{ 'opacity-50 cursor-not-allowed': isSaleFieldReadOnly(data, field) }"
                                                    @click.stop
                                                />
                                                <span class="w-30 flex-shrink-0 text-sm font-medium text-gray-600 px-2 py-1 bg-gray-100 rounded"
                                                      :class="{ 'opacity-50': isSaleFieldReadOnly(data, field) }">
                                                    {{ data.moeda || 'BRL' }}
                                                </span>
                                            </div>

                                            <!-- Anchor fields: input with 1-3 validation -->
                                            <InputText
                                                v-else-if="isCellEditable(data) && isAnchorField(field)"
                                                v-model="data[field]"
                                                class="w-full"
                                                placeholder="1"
                                                type="number"
                                                min="1"
                                                max="3"
                                                @keypress="(e) => {
                                                    const char = String.fromCharCode(e.which)
                                                    if (!/[123]/.test(char) && e.which !== 8 && e.which !== 9 && e.which !== 13 && e.which !== 27) {
                                                        e.preventDefault()
                                                    }
                                                }"
                                                @input="(e) => {
                                                    let value = e.target.value.replace(/[^123]/g, '')
                                                    if (value === '' || parseInt(value, 10) < 1 || parseInt(value, 10) > 3) {
                                                        value = '1'
                                                    }
                                                    e.target.value = value
                                                    data[field] = parseInt(value, 10)
                                                }"
                                                @blur="(e) => {
                                                    if (!e.target.value || ![1, 2, 3].includes(parseInt(e.target.value, 10))) {
                                                        e.target.value = '1'
                                                        data[field] = 1
                                                    }
                                                }"
                                            />

                                            <!-- Non-type fields: normal input -->
                                            <InputText
                                                v-else-if="isCellEditable(data)"
                                                v-model="data[field]"
                                                class="w-full"
                                            />
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>

                            <!-- Info Box -->
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <div class="flex gap-2">
                                    <i class="pi pi-info-circle text-blue-600 mt-0.5"></i>
                                    <div class="text-sm text-blue-900">
                                        <div class="font-semibold mb-1">{{ $t('Click on any cell to edit its value before importing') }}</div>
                                        <div class="text-xs text-blue-800">
                                            {{ $t('Values highlighted in red (strikethrough) will be replaced by values in green') }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Confirm Button -->
                            <div class="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 border-t border-surface-200">
                                <Button
                                    :label="$t('Cancel')"
                                    severity="secondary"
                                    outlined
                                    @click="cancelPreview"
                                    class="w-full sm:w-auto"
                                />
                                <Button
                                    :label="previewData.some(d => d._failed) ? $t('Retry Import') : $t('Confirm & Import')"
                                    :icon="previewData.some(d => d._failed) ? 'pi pi-refresh' : 'pi pi-check'"
                                    @click="confirmImport"
                                    class="w-full sm:w-auto"
                                />
                            </div>
                        </div>

                        <!-- Import Results -->
                        <div v-if="importFinished" class="flex flex-col gap-6">
                            <!-- Header com ícone de sucesso -->
                            <div class="text-center py-6">
                                <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                    <i class="pi pi-check-circle text-3xl text-green-600"></i>
                                </div>
                                <h2 class="text-2xl font-bold text-surface-900 mb-2">{{ $t('Import Completed Successfully!') }}</h2>
                                <p class="text-surface-600">{{ $t('Your sites have been processed and imported into the system') }}</p>
                            </div>

                            <!-- Resumo Geral -->
                            <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                                <h3 class="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                                    <i class="pi pi-chart-bar text-blue-600"></i>
                                    {{ $t('Import Summary') }}
                                </h3>
                                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div class="text-center">
                                        <div class="text-2xl font-bold text-blue-600">{{ importSummary?.totalProcessed || (importAdded?.length || 0) + (importUpdated?.length || 0) }}</div>
                                        <div class="text-sm text-blue-700">{{ $t('Total Processed') }}</div>
                                    </div>
                                    <div class="text-center" v-if="(importSummary?.added?.length || importAdded?.length) > 0">
                                        <div class="text-2xl font-bold text-green-600">{{ importSummary?.added?.length || importAdded?.length }}</div>
                                        <div class="text-sm text-green-700">{{ $t('Sites Added') }}</div>
                                    </div>
                                    <div class="text-center" v-if="(importSummary?.updated?.length || importUpdated?.length) > 0">
                                        <div class="text-2xl font-bold text-orange-600">{{ importSummary?.updated?.length || importUpdated?.length }}</div>
                                        <div class="text-sm text-orange-700">{{ $t('Sites Updated') }}</div>
                                    </div>
                                    <div class="text-center" v-if="(importSummary?.newSellers?.length || newSellersCreated?.length) > 0">
                                        <div class="text-2xl font-bold text-purple-600">{{ importSummary?.newSellers?.length || newSellersCreated?.length }}</div>
                                        <div class="text-sm text-purple-700">{{ $t('New Sellers') }}</div>
                                    </div>
                                </div>
                            </div>

                            <!-- Detalhes dos Sites Adicionados -->
                            <div v-if="(importSummary?.added?.length || importAdded?.length) > 0" class="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h4 class="text-md font-semibold text-green-800 mb-3 flex items-center gap-2">
                                    <i class="pi pi-plus-circle text-green-600"></i>
                                    {{ $t('New Sites Added') }} ({{ importSummary?.added?.length || importAdded?.length }})
                                </h4>
                                <div class="max-h-32 overflow-y-auto">
                                    <div class="grid gap-1">
                                        <div v-for="url in (importSummary?.added || importAdded || [])" :key="url" class="text-sm text-green-700 font-mono">
                                            <i class="pi pi-link text-xs mr-2"></i>{{ url }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Detalhes dos Sites Atualizados -->
                            <div v-if="(importSummary?.updated?.length || importUpdated?.length) > 0" class="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <h4 class="text-md font-semibold text-orange-800 mb-3 flex items-center gap-2">
                                    <i class="pi pi-refresh text-orange-600"></i>
                                    {{ $t('Sites Updated') }} ({{ importSummary?.updated?.length || importUpdated?.length }})
                                </h4>
                                <div class="max-h-32 overflow-y-auto">
                                    <div class="grid gap-1">
                                        <div v-for="url in (importSummary?.updated || importUpdated || [])" :key="url" class="text-sm text-orange-700 font-mono">
                                            <i class="pi pi-link text-xs mr-2"></i>{{ url }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Sellers Novos Criados -->
                            <div v-if="(importSummary?.newSellers?.length || newSellersCreated?.length) > 0" class="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <h4 class="text-md font-semibold text-purple-800 mb-3 flex items-center gap-2">
                                    <i class="pi pi-user-plus text-purple-600"></i>
                                    {{ $t('New Sellers Created') }} ({{ importSummary?.newSellers?.length || newSellersCreated?.length }})
                                </h4>
                                <div class="grid gap-2">
                                    <div v-for="seller in (importSummary?.newSellers || newSellersCreated || [])" :key="seller.name" class="flex items-center gap-2">
                                        <i class="pi pi-user text-purple-600 text-xs"></i>
                                        <span class="text-sm font-medium text-purple-800">{{ seller.name }}</span>
                                        <span class="text-xs text-purple-600" v-if="seller.email">({{ seller.email }})</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Error Details -->
                            <div v-if="importFailures && importFailures.length > 0" class="mt-4">
                                <h3 class="text-lg font-semibold text-surface-900 mb-3">{{ $t('Failed Rows') }}</h3>
                                <div class="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                                    <div v-for="(failure, index) in importFailures" :key="index" class="mb-3 last:mb-0">
                                        <div class="font-semibold text-red-800">{{ $t('Row') }} {{ failure.row }}</div>
                                        <ul class="list-disc list-inside text-sm text-red-700 mt-1">
                                            <li v-for="(error, errorIndex) in failure.errors" :key="errorIndex">{{ error }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="flex flex-col sm:flex-row gap-3 sm:justify-end mt-4">
                                <Button
                                    :label="$t('Import Another File')"
                                    icon="pi pi-upload"
                                    severity="secondary"
                                    @click="resetImport"
                                    class="w-full sm:w-auto"
                                />
                                <Button
                                    :label="$t('View Sites')"
                                    icon="pi pi-arrow-right"
                                    @click="goBack"
                                    class="w-full sm:w-auto"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>

    <!-- Display Value Update Confirmation Modal -->
    <Dialog
        v-model:visible="showDisplayValueModal"
        modal
        :header="$t('Update Display Value?')"
        :style="{ width: '500px' }"
    >
        <div v-if="displayValueModalData" class="flex flex-col gap-4">
            <p class="text-surface-700">
                {{ $t('A type.sale value greater than the current display value has been detected.') }}
            </p>

            <div class="bg-surface-50 border border-surface-200 rounded-lg p-3">
                <div class="flex flex-col gap-2">
                    <div class="flex justify-between items-center">
                        <span class="font-semibold text-surface-600">{{ $t('Current display value:') }}</span>
                        <span class="text-red-700 font-mono">
                            {{ formatValueWithCurrency(
                                displayValueModalData.currentDisplayValue,
                                displayValueModalData.currentDisplayCoin
                            ) }}
                        </span>
                    </div>
                    <div class="flex justify-between items-center">
                        <span class="font-semibold text-surface-600">{{ $t('New type.sale value:') }}</span>
                        <span class="text-green-700 font-mono">
                            {{ formatValueWithCurrency(
                                displayValueModalData.newSaleValue,
                                displayValueModalData.newSaleCoin
                            ) }}
                        </span>
                    </div>
                </div>
            </div>

            <p class="text-sm text-surface-600">
                {{ $t('Do you want to update the display value to the new highest value or keep the current value?') }}
            </p>
        </div>

        <template #footer>
            <div class="flex gap-2 justify-end">
                <Button
                    :label="$t('Keep current value')"
                    severity="secondary"
                    outlined
                    @click="keepCurrentDisplayValue"
                />
                <Button
                    :label="$t('Update to new value')"
                    severity="success"
                    @click="updateToNewDisplayValue"
                />
            </div>
        </template>
    </Dialog>

    <!-- Display Cost Update Confirmation Modal -->
    <Dialog
        v-model:visible="showDisplayCostModal"
        modal
        :header="$t('Update Display Cost?')"
        :style="{ width: '500px' }"
    >
        <div v-if="displayCostModalData" class="flex flex-col gap-4">
            <p class="text-surface-700">
                {{ $t('A type.cost value greater than the current display cost has been detected.') }}
            </p>

            <div class="bg-surface-50 border border-surface-200 rounded-lg p-3">
                <div class="flex flex-col gap-2">
                    <div class="flex justify-between items-center">
                        <span class="font-semibold text-surface-600">{{ $t('Current display cost:') }}</span>
                        <span class="text-red-700 font-mono">
                            {{ formatValueWithCurrency(
                                displayCostModalData.currentDisplayValue,
                                displayCostModalData.currentDisplayCoin
                            ) }}
                        </span>
                    </div>
                    <div class="flex justify-between items-center">
                        <span class="font-semibold text-surface-600">{{ $t('New type.cost value:') }}</span>
                        <span class="text-green-700 font-mono">
                            {{ formatValueWithCurrency(
                                displayCostModalData.newCostValue,
                                displayCostModalData.newCostCoin
                            ) }}
                        </span>
                    </div>
                </div>
            </div>

            <p class="text-sm text-surface-600">
                {{ $t('Do you want to update the display cost to the new highest value or keep the current cost?') }}
            </p>
        </div>

        <template #footer>
            <div class="flex gap-2 justify-end">
                <Button
                    :label="$t('Keep current cost')"
                    severity="secondary"
                    outlined
                    @click="keepCurrentDisplayCost"
                />
                <Button
                    :label="$t('Update to new cost')"
                    severity="success"
                    @click="updateToNewDisplayCost"
                />
            </div>
        </template>
    </Dialog>

    <!-- Modal para Sellers Novos -->
    <Dialog
        v-model:visible="showNewSellersModal"
        modal
        :header="$t('Sellers Analysis - Import Preparation')"
        :style="{ width: '700px', maxWidth: '95vw' }"
        :closable="false"
    >
        <div class="flex flex-col gap-5">
            <!-- Descrição Geral -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div class="flex gap-3">
                    <i class="pi pi-info-circle text-blue-600 mt-0.5"></i>
                    <div class="text-sm text-blue-900">
                        <p class="font-semibold mb-2">{{ $t('Seller Analysis Complete') }}</p>
                        <p class="mb-1">{{ $t('We have analyzed the sellers in your import file and found the following:') }}</p>
                    </div>
                </div>
            </div>

            <!-- Sellers Existentes (se houver) -->
            <div v-if="existingSellers.length > 0" class="bg-surface-50 border rounded-lg p-4">
                <h4 class="font-semibold mb-2 flex items-center gap-2">
                    <i class="pi pi-check-circle text-green-600"></i>
                    {{ $t('Existing Sellers') }} ({{ existingSellers.length }})
                </h4>
                <p class="text-sm mb-2">{{ $t('These sellers already exist in the system and will be used as-is:') }}</p>
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="seller in existingSellers"
                        :key="seller"
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                        <i class="pi pi-user text-xs mr-1"></i>
                        {{ seller }}
                    </span>
                </div>
            </div>

            <!-- Sellers Novos -->
            <div class="bg-surface-50 border rounded-lg p-4">
                <h4 class="font-semibold mb-2 flex items-center gap-2">
                    <i class="pi pi-user-plus text-orange-600"></i>
                    {{ $t('New Sellers to Create') }} ({{ newSellers.length }})
                </h4>
                <p class="text-sm mb-3">{{ $t('These sellers do not exist in the system and will be created. You can optionally provide email addresses:') }}</p>

                <div class="space-y-3">
                    <div v-for="seller in newSellers" :key="seller" class="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white rounded-lg border">
                        <div class="flex-1">
                            <div class="flex items-center gap-2 mb-1">
                                <i class="pi pi-user text-orange-600 text-sm"></i>
                                <label class="text-sm font-medium text-surface-700">
                                    {{ seller }}
                                </label>
                                <span class="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">{{ $t('New') }}</span>
                            </div>
                            <InputText
                                v-model="newSellersEmails[seller]"
                                type="email"
                                :placeholder="'Enter email for ' + seller + ' here ' + 'but is (optional)'"
                                class="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Informação Adicional -->
            <div class="bg-surface-50 border border-surface-200 rounded-lg p-3">
                <div class="text-xs text-surface-600">
                    <div class="flex items-start gap-2">
                        <i class="pi pi-lightbulb text-yellow-500 mt-0.5"></i>
                        <div>
                            <p class="font-medium mb-1">{{ $t('Important Information:') }}</p>
                            <ul class="list-disc list-inside space-y-1 ml-2">
                                <li>{{ $t('Email addresses are optional but recommended for better seller management') }}</li>
                                <li>{{ $t('New sellers will be created in the system and can be managed later') }}</li>
                                <li>{{ $t('Existing sellers will keep their current information unchanged') }}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex gap-2 justify-end">
                <Button
                    :label="$t('Cancel Import')"
                    severity="secondary"
                    outlined
                    @click="cancelNewSellers"
                />
                <Button
                    :label="$t('Continue with Import')"
                    severity="success"
                    icon="pi pi-arrow-right"
                    @click="confirmNewSellers"
                />
            </div>
        </template>
    </Dialog>

    <!-- Import Progress Modal -->
    <Dialog
        v-model:visible="showImportProgressModal"
        modal
        :header="$t('Importing Sites')"
        :style="{ width: '400px' }"
        :closable="false"
        :draggable="false"
    >
        <div class="flex flex-col gap-4">
            <div class="text-center">
                <i class="pi pi-spin pi-spinner text-2xl text-blue-500 mb-3"></i>
                <p class="text-surface-700">{{ importProgressMessage }}</p>
            </div>

            <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center text-sm">
                    <span class="text-surface-600">{{ $t('Progress') }}</span>
                    <span class="font-semibold text-surface-700" v-if="importProgressCurrent > 0">{{ Math.round((importProgressCurrent / importProgressTotal) * 100) }}%</span>
                    <span class="text-surface-600" v-else>{{ $t('Processing...') }}</span>
                </div>

                <ProgressBar
                    :value="importProgressCurrent > 0 && importProgressTotal > 0 ? Math.round((importProgressCurrent / importProgressTotal) * 100) : null"
                    :showValue="false"
                    mode="indeterminate"
                    v-if="importProgressCurrent === 0"
                    class="h-4"
                />
                <ProgressBar
                    :value="Math.round((importProgressCurrent / importProgressTotal) * 100)"
                    :showValue="true"
                    v-else
                    class="h-4"
                />
            </div>

            <div class="text-xs text-center text-surface-500">
                {{ $t('Please wait while we process your sites...') }}
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    :label="$t('Run in Background')"
                    severity="secondary"
                    outlined
                    @click="runInBackground"
                    :disabled="importProgressCurrent >= importProgressTotal && importProgressTotal > 0"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
/* Force DataTable to respect container width */
:deep(.p-datatable) {
    width: 100%;
    max-width: 100%;
}

:deep(.p-datatable-wrapper) {
    overflow-x: auto !important;
    max-width: 100%;
}

:deep(.p-datatable-table) {
    min-width: max-content;
}

/* Ensure frozen columns work properly */
:deep(.p-datatable.p-datatable-scrollable .p-datatable-frozen-tbody) {
    position: sticky;
    left: 0;
    z-index: 1;
}

/* Improved cell padding for comparison display */
:deep(.p-datatable-tbody > tr > td) {
    padding: 0.75rem 1rem;
}
</style>
